import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SectionTitle from "../ui/SectionTitle";
import { requestContactSubject } from "../ui/contactIntent";
import PromotionNote from "./PromotionNote";
import {
  SubscriptionPlan,
  PlanLimits,
  SELECTED_PLAN_KEY,
  pricingByPlan,
  currencySymbol,
  currencyTrails,
  formatMoney,
  formatPrice,
  hasDiscount,
  hasSeatDiscount,
  BillingPeriod,
  ANNUAL_FREE_MONTHS,
} from "../../constants/subscription";
import { GetPublicPricingAction } from "../../store/action";

/**
 * Pricing Component
 * The public grid: three tiers you can buy, and a fourth card that is a
 * conversation rather than a price.
 *
 * The numbers come from `GET /api/Pricing`, not from this file. A SystemOwner
 * can change a plan's price and run promotions against it, and the wizard and
 * the dashboard plan panel read the same endpoint — a price hard-coded here
 * would be the one place that kept advertising last month's figure.
 *
 * The tier that sells seats states the per-seat price as a line of text under
 * its headline number. It used to carry a stepper that quoted a total for a
 * team of N; that is gone deliberately — nothing charges yet, so the arithmetic
 * was decoration, and the grid reads faster without it.
 *
 * A toggle above the grid switches every card to a year paid up front, billed
 * as ten months ("2 months free"). The yearly figures come from the server with
 * the monthly ones, and a promotion's percentage is off the seats as well as
 * the plan — in both modes.
 *
 * The card shows both prices whenever something is on offer: the list price
 * crossed out and the discounted one beside it. A plan discounted to nothing
 * shows "0" rather than the word "free" — free is what the Start tier *is*,
 * and a launch offer that ends is a different thing that must not look
 * identical to it.
 *
 * The promotion's name opens a note with its deadline and a link to the terms —
 * see PromotionNote.
 *
 * Only features that exist are listed, with one deliberate exception: the
 * Business card. It advertises a custom domain, email from the customer's own
 * domain, online payments and deposits, white-label branding, advanced
 * automations and reports, and assisted onboarding — most of which is not
 * built (the booking page lives at `/:slug`, `SmtpEmailSender` has one global
 * From address, NETOPIA only takes our own subscription payments). They are
 * sold first and built per deal (2026-09-16, extended 2026-09-30 — the owner's
 * call), which the quote-only card makes safe: nobody can buy any of it without
 * a conversation happening first. Do not "fix" this by deleting those lines.
 * On the three priced cards the rule stands — anything a visitor cannot be
 * shown in the running app does not belong there, whatever the roadmap says.
 *
 * If the request fails the page falls back to the prices the site shipped with
 * (`PlanPrice` in constants/subscription), because a pricing page with no
 * prices on it is worse than a pricing page with slightly stale ones.
 *
 * @param {string} sectionTitle - Main section title
 * @param {string} sectionDescription - Section description
 * @param {Array} pricingPlans - Array of pricing plan objects
 */
const Pricing = ({ sectionTitle, sectionDescription, pricingPlans = [] }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    let cancelled = false;

    GetPublicPricingAction()
      .then((data) => {
        if (!cancelled) setPricing(data);
      })
      // Deliberately silent: `quotes` already falls back to the shipped prices,
      // so there is nothing for a visitor to do about this and nothing useful
      // to tell them.
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const quotes = useMemo(() => pricingByPlan(pricing), [pricing]);
  const period = t("Pricing.period");

  // Monthly or a year up front. A year is billed as ten months — "2 months
  // free" — and the cards show the exact yearly figures the server quotes, so
  // what the toggle promises is what the checkout charges.
  const [billing, setBilling] = useState(BillingPeriod.MONTHLY);
  const annual = billing === BillingPeriod.ANNUAL;
  const freeMonths = pricing?.annualFreeMonths ?? ANNUAL_FREE_MONTHS;

  const feature = (key, opts) => ({ text: t(key, opts), available: true });

  // "Everything in Essential" heads the next tier's list. The name is the plan's
  // own translation, not a word in the sentence, so renaming a plan cannot
  // leave the card below it pointing at a tier that no longer exists.
  const everythingIn = (planKey) => ({
    text: t("Pricing.everythingIn", { plan: t(`Pricing.plans.${planKey}.name`) }),
    available: true,
    emphasis: true,
  });

  // "1 specialist", "3 specialists": the seats the headline price covers. The
  // count rides on the quote like the per-seat price under it, so the card
  // cannot promise three people while the checkout bills for two.
  const specialists = (plan) =>
    feature("Pricing.includedSpecialists", {
      count: quotes[plan]?.includedSeats ?? 0,
    });

  // "50 SMS included": the monthly allowance spent before any bought package.
  // Read from the PlanLimits mirror rather than typed into the copy, so the card
  // and the SMS page's meter cannot disagree. It is a code constant, not a
  // SystemOwner setting, so it does not ride on the quote.
  const includedSms = (plan) =>
    feature("Pricing.includedSms", {
      count: PlanLimits[plan]?.includedSmsPerMonth ?? 0,
    });

  // The trial, from the server for the same reason: a SystemOwner can change it
  // from the pricing tab, and a number typed into marketing copy would not
  // follow. Zero means no trial, and the line is dropped rather than promising
  // "0 days free".
  const trialDays = pricing?.trialDays ?? 0;

  // The four cards, in the order the grid shows them. Business is the last one
  // and is no longer a tier you can buy: it carries no number, it lists only
  // what the priced plans do not have, and its CTA goes to the contact form
  // rather than to signup.
  const defaultPricingPlans = [
    {
      id: 1,
      name: t("Pricing.plans.starter.name"),
      planValue: SubscriptionPlan.STARTER,
      period,
      recommended: false,
      features: [
        // One specialist, and on this tier it is a ceiling rather than an
        // included count — there is no per-seat line under it to buy a second.
        specialists(SubscriptionPlan.STARTER),
        feature("Pricing.plans.starter.features.bookings"),
        feature("Pricing.plans.starter.features.bookingPage"),
        feature("Pricing.plans.starter.features.emailNotifications"),
        feature("Pricing.plans.starter.features.reports"),
        { text: t("Pricing.plans.starter.features.notListed"), available: false },
      ],
      buttonText: t("Pricing.buyButton"),
      buttonLink: "#",
    },
    {
      id: 2,
      name: t("Pricing.plans.essential.name"),
      planValue: SubscriptionPlan.ESSENTIAL,
      period,
      recommended: false,
      features: [
        specialists(SubscriptionPlan.ESSENTIAL),
        includedSms(SubscriptionPlan.ESSENTIAL),
        feature("Pricing.plans.essential.features.bookingPage"),
        // What a paid plan adds over Start's page: listed businesses are the
        // ones the sitemap submits (PublicSitemap), on top of the title,
        // description and canonical every booking page already sets.
        feature("Pricing.plans.essential.features.seo"),
        feature("Pricing.plans.essential.features.calendar"),
        feature("Pricing.plans.essential.features.services"),
        feature("Pricing.plans.essential.features.customers"),
        feature("Pricing.plans.essential.features.availability"),
        feature("Pricing.plans.essential.features.emailConfirmations"),
        feature("Pricing.plans.essential.features.pageCustomisation"),
        feature("Pricing.plans.essential.features.embed"),
      ],
      buttonText: t("Pricing.buyButton"),
      buttonLink: "#",
    },
    {
      id: 3,
      name: t("Pricing.plans.professional.name"),
      planValue: SubscriptionPlan.PROFESSIONAL,
      period,
      recommended: true,
      features: [
        everythingIn("essential"),
        specialists(SubscriptionPlan.PROFESSIONAL),
        includedSms(SubscriptionPlan.PROFESSIONAL),
        feature("Pricing.plans.professional.features.team"),
        feature("Pricing.plans.professional.features.calendars"),
        feature("Pricing.plans.professional.features.resources"),
        feature("Pricing.plans.professional.features.reports"),
        feature("Pricing.plans.professional.features.extendedCustomisation"),
      ],
      buttonText: t("Pricing.buyButton"),
      buttonLink: "#",
    },
    {
      id: 4,
      name: t("Pricing.plans.business.name"),
      custom: true,
      period,
      recommended: false,
      features: [
        everythingIn("professional"),
        // No number: Business's allowance is whatever the contract says
        // (`includedSmsByAgreement`).
        feature("Pricing.plans.business.features.sms"),
        feature("Pricing.plans.business.features.domain"),
        feature("Pricing.plans.business.features.email"),
        feature("Pricing.plans.business.features.payments"),
        feature("Pricing.plans.business.features.branding"),
        feature("Pricing.plans.business.features.unbranded"),
        feature("Pricing.plans.business.features.automations"),
        feature("Pricing.plans.business.features.advancedReports"),
        feature("Pricing.plans.business.features.support"),
        feature("Pricing.plans.business.features.onboarding"),
      ],
      buttonText: t("Pricing.quoteButton"),
      buttonLink: "#contact",
      contactSubject: t("Pricing.contactSubject"),
    },
  ];

  const plansToDisplay =
    pricingPlans.length > 0 ? pricingPlans : defaultPricingPlans;

  /**
   * The quote behind a card.
   *
   * A caller passing its own `pricingPlans` may hand us a literal `price`; that
   * wins, so the prop stays a genuine override rather than something the fetch
   * quietly overwrites. Such a card sells no seats — the override says what it
   * costs, and inventing seat terms for it would contradict that.
   */
  const quoteFor = (plan) => {
    if (plan.price != null) {
      return {
        listPrice: plan.price,
        price: plan.price,
        discountPercent: 0,
        currency: plan.currency || "EUR",
        promotionName: null,
        promotionEndsAt: null,
        extraSeatPrice: null,
      };
    }
    return plan.planValue != null ? quotes[plan.planValue] : null;
  };

  /**
   * The headline number, which is not always a number. The free tier shows the
   * word rather than a bare "0" that reads like a missing value, inside the same
   * h4 so every card keeps the same name/price shape.
   *
   * When a promotion applies, the list price is struck through above the price
   * being charged — including when the discount takes it to zero, which is the
   * one case where "0" is the honest thing to print. A permanently free plan
   * says "Free"; a plan that is free until Monday shows the number it will cost
   * afterwards.
   */
  const renderPrice = (plan, quote) => {
    const symbol = currencySymbol(quote?.currency);
    // "lei" follows the number, "€" leads it. The headline price is two
    // elements of different sizes, so it needs the position rather than the
    // joined string formatMoney returns.
    const trails = currencyTrails(quote?.currency);
    const discounted = hasDiscount(quote);

    // Free by design, not by offer.
    if (!discounted && quote?.price === 0) {
      return (
        <h4 className="mt-4 text-ink-900">
          <span className="text-5xl font-bold leading-none">
            {t("Pricing.free")}
          </span>
        </h4>
      );
    }

    // A year up front: the yearly figure as the headline, twelve months at list
    // price struck through above it — "€290 instead of €348" — with the monthly
    // equivalent under it. A card overridden with a literal price carries no
    // yearly figure and stays monthly.
    const yearly = annual && quote?.annualPrice != null;
    const shown = yearly ? quote.annualPrice : (quote?.price ?? 0);
    const struck = yearly ? quote.annualListPrice : quote?.listPrice;
    const showStrike = yearly ? struck != null && struck > shown : discounted;

    return (
      <>
        {showStrike && (
          <p className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <span
              className="text-ink-400 line-through"
              data-testid={`list-price-${plan.planValue}`}
            >
              {formatMoney(struck, quote.currency)}
            </span>
            {discounted && (
              <span className="rounded-full bg-brand-600 px-2 py-0.5 text-xs font-bold text-white">
                −{quote.discountPercent}%
              </span>
            )}
            {yearly && freeMonths > 0 && (
              <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-bold text-brand-700">
                {t("Pricing.billing.freeMonths", { count: freeMonths })}
              </span>
            )}
          </p>
        )}

        <h4
          className={`flex items-baseline gap-1 text-ink-900 ${
            showStrike ? "mt-1" : "mt-4"
          }`}
        >
          {!trails && (
            <sup className="text-lg font-semibold text-ink-400">{symbol}</sup>
          )}
          <span
            className="text-5xl font-bold tabular-nums leading-none"
            data-testid={`price-${plan.planValue}`}
          >
            {formatPrice(shown)}
          </span>
          {trails && (
            <span className="text-lg font-semibold text-ink-400">{symbol}</span>
          )}
          <span className="text-sm font-medium text-ink-400">
            / {yearly ? t("Pricing.periodYear") : plan.period || period}
          </span>
        </h4>

        {yearly && shown > 0 && (
          <p
            className="m-0 mt-1.5 text-xs text-ink-500"
            data-testid={`annual-equivalent-${plan.planValue}`}
          >
            {t("Pricing.annualEquivalent", {
              price: formatMoney(Math.round((shown / 12) * 100) / 100, quote.currency),
            })}
          </p>
        )}

        {discounted && (
          <PromotionNote
            name={quote.promotionName}
            endsAt={quote.promotionEndsAt}
            planValue={plan.planValue}
          />
        )}
      </>
    );
  };

  /**
   * What one more person costs, on the tiers that sell seats beyond the headline
   * price. A line of text under the number, not a calculator: nothing is charged
   * yet, so a total quoted for a team of six would be a promise the product
   * cannot keep.
   *
   * The price still comes from the quote rather than from this file, for the
   * same reason the headline number does.
   */
  const renderSeatNote = (plan, quote) => {
    if (!quote || quote.extraSeatPrice == null) return null;

    // The plan's promotion is off the seats too, so under "−50%" the seat line
    // shows the halved price with the full one struck through beside it. Yearly,
    // the struck figure is twelve months at list — the same comparison the
    // headline makes.
    const yearly = annual && quote.annualExtraSeatPrice != null;
    const seatPrice = formatMoney(
      yearly ? quote.annualExtraSeatPrice : quote.extraSeatPrice,
      quote.currency,
    );
    const struck = yearly
      ? quote.extraSeatListPrice * 12
      : hasSeatDiscount(quote)
        ? quote.extraSeatListPrice
        : null;
    const seatListPrice =
      struck != null ? formatMoney(struck, quote.currency) : null;

    return (
      <p
        className="m-0 mt-2 text-fluid-sm font-medium text-ink-500"
        data-testid={`seat-note-${plan.planValue}`}
      >
        {seatListPrice && (
          <span
            className="mr-1.5 text-ink-400 line-through"
            data-testid={`seat-list-price-${plan.planValue}`}
          >
            {seatListPrice}
          </span>
        )}
        {t(yearly ? "Pricing.perSeatYear" : "Pricing.perSeat", {
          price: seatPrice,
        })}
      </p>
    );
  };

  /**
   * Monthly or yearly. Two buttons rather than a switch, because both options
   * need their own label and the yearly one carries the "2 months free" that is
   * the reason to press it.
   */
  const renderBillingToggle = () => (
    <div className="mt-8 flex justify-center">
      <div
        role="group"
        aria-label={t("Pricing.billing.label")}
        className="inline-flex rounded-full border border-ink-200 bg-white p-1 shadow-card"
      >
        {[BillingPeriod.MONTHLY, BillingPeriod.ANNUAL].map((value) => {
          const active = billing === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              data-testid={`billing-${value === BillingPeriod.ANNUAL ? "annual" : "monthly"}`}
              onClick={() => setBilling(value)}
              className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                active ? "bg-brand-600 text-white" : "text-ink-600 hover:text-brand-700"
              }`}
            >
              {value === BillingPeriod.ANNUAL
                ? t("Pricing.billing.annual")
                : t("Pricing.billing.monthly")}
              {value === BillingPeriod.ANNUAL && freeMonths > 0 && (
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                    active ? "bg-white/20 text-white" : "bg-brand-50 text-brand-700"
                  }`}
                >
                  {t("Pricing.billing.freeMonths", { count: freeMonths })}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  /**
   * "14 days free, no card required" — on the plans it is true of.
   *
   * Not on the free tier, which has nothing to try, and not on the quoted card,
   * which has no self-service signup for a trial to start. Dropped entirely when
   * the trial is set to zero days rather than advertising "0 days free".
   */
  const renderTrialNote = (plan, quote) => {
    if (trialDays <= 0) return null;
    if (plan.custom) return null;
    if (!quote || !quote.price) return null;

    return (
      <p
        className="m-0 mt-1.5 text-fluid-sm font-semibold text-brand-700"
        data-testid={`trial-note-${plan.planValue}`}
      >
        <i className="bi bi-gift mr-1" aria-hidden="true" />
        {t("Pricing.trial", { count: trialDays })}
      </p>
    );
  };

  return (
    <section id="pricing" className="pricing bg-ink-50 py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionTitle
          title={sectionTitle || t("Pricing.title")}
          description={sectionDescription || t("Pricing.description")}
        />

        {renderBillingToggle()}

        {/* Four cards: 2×2 on tablets, a single row on desktop. items-start keeps
            the recommended card's scale-up from stretching its siblings. */}
        <div className="mt-14 grid items-start gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plansToDisplay.map((plan) => {
            const quote = plan.custom ? null : quoteFor(plan);

            return (
              <div
                key={plan.id}
                className={`box relative flex h-full flex-col rounded-card border bg-white p-8 transition duration-200 ${
                  plan.recommended
                    ? "recommended border-brand-600 shadow-brand lg:-my-2 lg:py-10"
                    : "border-ink-200 shadow-card hover:border-brand-200 hover:shadow-card-hover"
                }`}
              >
                {plan.recommended && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                    {t("Pricing.recommended", "Most popular")}
                  </span>
                )}

                <h3 className="text-sm font-bold uppercase tracking-wider text-brand-700">
                  {plan.name}
                </h3>

                {plan.custom ? (
                  <h4 className="mt-4 text-2xl font-bold leading-tight text-ink-900 sm:text-3xl">
                    {t("Pricing.contactPrice")}
                  </h4>
                ) : (
                  <>
                    {renderPrice(plan, quote)}
                    {renderSeatNote(plan, quote)}
                    {renderTrialNote(plan, quote)}
                  </>
                )}

                <ul className="mt-7 flex-1 space-y-3 border-t border-ink-100 pt-7">
                  {plan.features.map((planFeature, idx) => (
                    <li
                      key={idx}
                      className={`flex items-start gap-2.5 text-fluid-sm ${
                        !planFeature.available
                          ? "na text-ink-300 line-through"
                          : planFeature.emphasis
                            ? "font-semibold text-ink-900"
                            : "text-ink-600"
                      }`}
                    >
                      <i
                        className={`bi mt-0.5 shrink-0 ${
                          planFeature.available
                            ? "bi-check-circle-fill text-brand-600"
                            : "bi-x-circle text-ink-300"
                        }`}
                        aria-hidden="true"
                      />
                      <span>{planFeature.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="btn-wrap mt-8">
                  <a
                    href={plan.buttonLink}
                    className={`flex w-full items-center justify-center rounded-full px-6 py-3 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                      plan.recommended
                        ? "bg-brand-600 text-white hover:bg-brand-700"
                        : "border border-ink-300 text-ink-700 hover:border-brand-600 hover:text-brand-700"
                    }`}
                    onClick={(e) => {
                      // Quoted plan: preload the contact form's subject before we
                      // move to it, so the enquiry lands labelled.
                      if (plan.contactSubject) {
                        requestContactSubject(plan.contactSubject);
                      }

                      // A priced plan's CTA starts sign-up with that plan
                      // preselected — it's carried through to the setup wizard,
                      // where the organization (and its subscription) is created.
                      // The price is resolved there, server-side, so a stale card
                      // in a tab left open overnight cannot sell yesterday's
                      // promotion.
                      if (plan.planValue != null) {
                        e.preventDefault();
                        localStorage.setItem(
                          SELECTED_PLAN_KEY,
                          String(plan.planValue),
                        );
                        navigate("/signup");
                        return;
                      }

                      const anchor =
                        plan.buttonLink && plan.buttonLink.startsWith("#")
                          ? plan.buttonLink.slice(1)
                          : "";

                      if (anchor) {
                        // Scroll ourselves rather than trust the native hash jump:
                        // the header nav overrides it site-wide (handleNavClick in
                        // Menu.jsx), so a bare anchor would behave inconsistently.
                        e.preventDefault();
                        document
                          .getElementById(anchor)
                          ?.scrollIntoView({ behavior: "smooth" });
                      } else if (plan.buttonLink === "#") {
                        // Placeholder CTA with nowhere to go yet.
                        e.preventDefault();
                      }
                    }}
                  >
                    {plan.buttonText}
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Under the grid, because it qualifies every number above it. */}
        <div className="mt-10 flex flex-col items-center gap-2 text-center">
          <p className="m-0 text-fluid-sm text-ink-500">
            {t("Pricing.vatNote")}
          </p>
          {/* NETOPIA requires the transaction currency to be stated on the
              site, and it is not the currency in the grid above: the list is in
              euro and the card is charged in lei. Saying so here, rather than
              only at checkout, is the difference between a price somebody can
              plan around and a surprise on their statement. */}
          <p className="currency-note m-0 text-xs text-ink-400">
            {t("Pricing.currencyNote")}
          </p>
        </div>
      </div>
    </section>
  );
};

Pricing.propTypes = {
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
  pricingPlans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      // Optional: a card normally takes its price from the API, keyed on
      // `planValue`. Passing `price` overrides that for this card.
      price: PropTypes.number,
      planValue: PropTypes.number,
      custom: PropTypes.bool,
      currency: PropTypes.string,
      period: PropTypes.string,
      recommended: PropTypes.bool,
      features: PropTypes.arrayOf(
        PropTypes.shape({
          text: PropTypes.string.isRequired,
          available: PropTypes.bool.isRequired,
          // Set on "Everything in <tier>", which heads a list rather than
          // being one more item in it.
          emphasis: PropTypes.bool,
        }),
      ).isRequired,
      buttonText: PropTypes.string,
      buttonLink: PropTypes.string,
      // Subject to preload into the contact form when the CTA is clicked.
      contactSubject: PropTypes.string,
    }),
  ),
};

export default Pricing;
